import { apiClient } from './api'

export interface OrbcommStatus {
  modemId: string
  state: string
  enabled: boolean
  autoActivation: boolean
  gatewayAccountId?: string
  partNumber?: string
  softwareVersion?: string
  hardwareVersion?: string
  statusMessage: string
}

export interface OrbcommMessage {
  forwardMessageId: string
  stateUtc: string
  state: number
  isClosed: boolean
  transport?: string
  referenceNumber?: string
  errorId?: number
}

const MESSAGE_STATES: Record<number, string> = {
  0: 'Submitted',
  1: 'Received',
  2: 'Error',
  3: 'Delivery Failed',
  4: 'Timed Out',
  5: 'Cancelled',
  6: 'Waiting',
  7: 'N/A',
  8: 'Transmitted',
}

export const getMessageStateLabel = (state: number): string =>
  MESSAGE_STATES[state] ?? `Unknown (${state})`

export const PREDEFINED_MESSAGES = [
  { value: '100:IDP', label: 'Modem - Ping Request' },
  { value: '102:IDP', label: 'Modem - Get Position' },
  { value: '103:IDP', label: 'Modem - Reset (Preserve)' },
  { value: '104:IDP', label: 'Modem - Reset (Flush)' },
  { value: '105:IDP', label: 'Modem - Reset (Terminal)' },
  { value: '200:ALL', label: 'System - Get Terminal Info' },
  { value: '201:ALL', label: 'System - Get Terminal Status' },
  { value: '202:ALL', label: 'Position - Get Position' },
  { value: '203:ALL', label: 'Position - Get Last Position' },
  { value: '300:IDP', label: 'AVL - Get Position' },
]

export const orbcommService = {
  getTerminalStatus: async (deviceId: string): Promise<OrbcommStatus> => {
    const { data } = await apiClient.get(`/devices/${deviceId}/orbcomm/status`)
    return {
      modemId: data.modem_id,
      state: data.state,
      enabled: data.enabled,
      autoActivation: data.auto_activation,
      gatewayAccountId: data.gateway_account_id,
      partNumber: data.part_number,
      softwareVersion: data.software_version,
      hardwareVersion: data.hardware_version,
      statusMessage: data.status_message || data.message,
    }
  },

  activateTerminal: async (deviceId: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await apiClient.post(`/devices/${deviceId}/orbcomm/activate`)
    return data
  },

  deactivateTerminal: async (deviceId: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await apiClient.post(`/devices/${deviceId}/orbcomm/deactivate`)
    return data
  },

  sendMessage: async (deviceId: string, messageIdentifier: string, userMessageId?: number): Promise<{ success: boolean; message: string }> => {
    const { data } = await apiClient.post(`/devices/${deviceId}/orbcomm/message`, {
      message_identifier: messageIdentifier,
      user_message_id: userMessageId,
    })
    return data
  },

  getMessageHistory: async (deviceId: string): Promise<OrbcommMessage[]> => {
    const { data } = await apiClient.get(`/devices/${deviceId}/orbcomm/messages`)
    return (data.messages || data || []).map((m: any) => ({
      forwardMessageId: m.forward_message_id || m.ForwardMessageID,
      stateUtc: m.state_utc || m.StateUTC,
      state: m.state ?? m.State,
      isClosed: m.is_closed ?? m.IsClosed,
      transport: m.transport || m.Transport,
      referenceNumber: m.reference_number || m.ReferenceNumber,
      errorId: m.error_id || m.ErrorID,
    }))
  },
}
